<script setup>
import { onMounted, ref, watch, nextTick, onUnmounted } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";
const router = useRouter();
const $loading = useLoading(loaderData);

const workflowData = ref({
    title: "",
    description: "",
    status: "draft",
    requireContact: 'optional',
    requireUser: 'optional',
    requireLicense: 'no',
    licenseNo: null,
    type: null,
    workflowCategoryId: null,
});

const workflowCategories = ref([]);
const licenses = ref([]);

const statusOptions = [
    {
        title: "Draft",
        value: "draft",
    },
    {
        title: "Approved",
        value: "approved",
    },
    {
        title: "Suspended",
        value: "suspended",
    },  
];
const RequireContactOptions = [
    {
        title: "Optional",
        value: "optional",
    },
    {
        title: "Required",
        value: "required",
    },
    {
        title: "Hidden",
        value: "hidden",
    }
];
const RequireUserOptions = [
    {
        title: "Optional",
        value: "optional",
    },
    {
        title: "Required",
        value: "required",
    },
    {
        title: "Hidden",
        value: "hidden",
    }
];

const RequireLicenseOptions = [
    {
        title: "Yes",
        value: "yes",
    },
    {
        title: "No",
        value: "no",
    }
];

const typeOptions = [
    {
        title: "Client",
        value: "client",
    },
    {
        title: "Operations",
        value: "operations",
    },
    {
        title: "Rep",
        value: "rep",
    },
    {
        title: "Branch Office",
        value: "branch_office",
    },  
];

const submitBtn = ref(1);

onMounted(async () => {
    getWorkflowCategories();
    getLicenses();
});

const getLicenses = () => {
    axios
        .get('/licenses?per_page=100')
        .then((response) => {
            if (response.status === 'success') {
                licenses.value = (response.data.licenses?.data || []).map((license) => ({
                    title: license.name,
                    value: license.number,
                }));
            }
        })
        .catch(() => {});
};

const getWorkflowCategories = () => {
    axios
        .get('/workflow-categories?all=true&status=active')
        .then((response) => {
            if (response.status === 'success') {
                workflowCategories.value = (response.data || []).map((category) => ({
                    title: category.name,
                    value: category.id,
                }));
            }
        })
        .catch(() => {});
};

const createWorkflow = async () => {
    let loader = $loading.show();
    submitBtn.value = 0;
    axios
        .post("/workflows", workflowData.value)
        .then((response) => {
            if (response.status == "success") {
                toast.success(response.message, {
                    autoClose: 5000,
                });
                router.push({ name: "workflows-manage" });
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
            submitBtn.value = 1;
        });
};

const addSteps = () => {
    let loader = $loading.show();
    submitBtn.value = 0;
    axios
        .post("/workflows", workflowData.value)
        .then((response) => {
            if (response.status == "success") {
                toast.success(response.message, {
                    autoClose: 5000,
                });
                router.push({ name: "workflows-id", params: { id: response.data.workflow.id }, query: { addSteps: true } });
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
            submitBtn.value = 1;
        });
}

const discardChanges = () => {
    router.push({ name: "workflows-manage" });
}

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div class="create-workflow-page">
    <!-- Header -->
    <div class="page-header d-flex flex-wrap justify-space-between align-center gap-3 mb-5">
      <div>
        <h2 class="page-title mb-1">Create New Workflow</h2>
        <p class="page-subtitle mb-0">Set up workflow information before adding steps.</p>
      </div>

      <div class="d-flex gap-2">
        <VBtn variant="tonal" color="dark" @click.prevent="discardChanges">
          Discard
        </VBtn>
        <VBtn color="primary" @click.prevent="createWorkflow">
          Create Workflow
        </VBtn>
      </div>
    </div>

    <VCard class="workflow-info-card" rounded="lg">
      <VCardItem class="pb-0">
        <VCardTitle class="text-h6 font-weight-bold">Workflow Information</VCardTitle>
      </VCardItem>

      <VCardText class="pt-4">
        <VForm @submit.prevent="createWorkflow">
          <VRow class="row-gap">
            <!-- Line 1 -->
            <VCol cols="12" md="8">
              <AppTextField
                v-model="workflowData.title"
                label="Title *"
                placeholder="Enter workflow title"
                :rules="[requiredValidator]"
              />
            </VCol>
            <VCol cols="12" md="4">
              <AppSelect
                v-model="workflowData.status"
                :items="statusOptions"
                label="Status *"
                :rules="[requiredValidator]"
              />
            </VCol>

            <!-- Line 2 -->
            <VCol cols="12" md="3">
              <AppSelect
                v-model="workflowData.requireContact"
                :items="RequireContactOptions"
                label="Require Contact *"
                :rules="[requiredValidator]"
              />
            </VCol>
            <VCol cols="12" md="3">
              <AppSelect
                v-model="workflowData.requireUser"
                :items="RequireUserOptions"
                label="Require User *"
                :rules="[requiredValidator]"
              />
            </VCol>            
            <VCol cols="12" md="3">
              <AppSelect
                v-model="workflowData.type"
                :items="typeOptions"
                label="Type"
                clearable
                placeholder="Select type"
              />
            </VCol>
            <VCol cols="12" md="3">
              <AppSelect
                v-model="workflowData.workflowCategoryId"
                :items="workflowCategories"
                label="Category"
                clearable
                placeholder="Select category"
              />
            </VCol>
            <VCol cols="12" md="3">
              <AppSelect
                v-model="workflowData.requireLicense"
                :items="RequireLicenseOptions"
                label="Require License *"
                :rules="[requiredValidator]"
              />
            </VCol>

            <!-- Line 3 -->
            <VCol v-if="workflowData.requireLicense === 'yes'" cols="12">
              <AppSelect
                v-model="workflowData.licenseNo"
                :items="licenses"
                label="License *"
                clearable
                placeholder="Select license"
                :rules="[requiredValidator]"
              />
            </VCol>

            <!-- Line 4 -->
            <VCol cols="12">
              <AppTextarea
                rows="4"
                v-model="workflowData.description"
                label="Description"
                placeholder="Add description (optional)"
              />
            </VCol>
          </VRow>

          <VDivider class="my-5" />

          <div class="d-flex flex-wrap justify-end gap-2">
            <VBtn variant="tonal" color="dark" @click.prevent="discardChanges">
              Cancel
            </VBtn>
            <VBtn color="primary" variant="tonal" @click.prevent="addSteps">
              Save & Add Steps
              <VIcon icon="tabler-arrow-right" end />
            </VBtn>
            <VBtn color="primary" @click.prevent="createWorkflow">
              Save Workflow
            </VBtn>
          </div>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.create-workflow-page {
  width: 100%;
  max-width: none; /* remove giới hạn gây dư bên phải */
}

.page-header {
  padding: 0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}

.page-subtitle {
  color: rgba(var(--v-theme-on-surface), 0.68);
  font-size: 0.92rem;
}

.workflow-info-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  box-shadow: 0 10px 28px rgba(20, 20, 43, 0.06);
}

.row-gap {
  row-gap: 4px;
}

:deep(.v-field) {
  border-radius: 10px;
}

:deep(.v-field__input) {
  min-height: 42px;
}

:deep(textarea.v-field__input) {
  min-height: 120px;
}
</style>
